import { groupBy, orderBy } from 'lodash'
import { DateTime, Interval } from 'luxon'
import { type Project } from '@/classes/entities/Project'
import { type ProjectDate } from '@/classes/entities/ProjectDate'
import { OccurrenceGenerator } from './recurrence/occurrence-generator'

export enum WorkItemType {
  DATE = 0,
  PROJECT = 1,
}
export type TWorkItem = {
  start: DateTime | null
  end: DateTime | null
  title: string
  subtitle: string | null
  customer: string | null
  number: string | null
  location: string | null
  project_id: number | null
  project_date_id: number | null
  type: WorkItemType
  completed_date: string | null
}
export type TSchedule = {
  date: string
  workItems: TWorkItem[]
}[]

export function getSchedule(
  start: DateTime, // includes start day
  end: DateTime, // until (but excluding) end day
  projects: Project[],
): TSchedule {
  const days = Interval.fromDateTimes(start, end)
    .splitBy({ day: 1 })
    .map((d) => d.start)

  const schedule = createSchedule(days, projects)
  return schedule
}

function createSchedule(days: DateTime[], projects: Project[]): TSchedule {
  let schedule = days.map((day) => {
    return {
      date: day.startOf('day').toISO(),
      workItems: [] as TWorkItem[],
    }
  })
  for (const project of projects) {
    for (const projectDate of project.project_dates || []) {
      addProjectDateWorkItemsToSchedule(project, projectDate, schedule)
    }
    if (!project.project_dates || project.project_dates.length === 0) {
      // when a project has projectdates it should never be scheduled
      // becaus it acts as just a container for projectdates and
      // is usually not used as a task itself
      addProjectWorkItemsToSchedule(project, schedule)
    }
  }
  schedule = schedule.map((day) => {
    return {
      date: day.date,
      workItems: orderBy(orderBy(day.workItems, 'start'), 'type'),
    }
  })
  return schedule
}

function addProjectWorkItemsToSchedule(project: Project, schedule: TSchedule) {
  const scheduleStartDate = DateTime.fromISO(schedule[0].date)
  const scheduleEndDate = DateTime.fromISO(schedule[schedule.length - 1].date)
  const projectStart = project.start_date
    ? DateTime.fromISO(project.start_date)
    : undefined
  const projectEnd = project.end_date
    ? DateTime.fromISO(project.end_date)
    : undefined
  if (project.rrule) {
    const projectRecurrences = getRecurringWorkItems(
      project,
      null,
      scheduleStartDate,
      scheduleEndDate,
    )

    for (const projectRecurrence of projectRecurrences) {
      addToSchedule(projectRecurrence, schedule)
    }
  }

  // const workItem = createWorkItem(project, null, projectStart, projectEnd)
  // addToSchedule(workItem, schedule)
  for (const [i, day] of schedule.entries()) {
    const date = DateTime.fromISO(day.date)
    const dayHasWorkItemForProject = day.workItems.find(
      (item) => item.project_id === project.id,
    )
    const projectStartsBeforeOrOnDay =
      projectStart && projectStart.startOf('day') <= date
    const projectEndsAfterOrOnDay =
      projectEnd && projectEnd.startOf('day') >= date
    if (
      (!dayHasWorkItemForProject &&
        projectStartsBeforeOrOnDay &&
        (projectEndsAfterOrOnDay || !projectEnd)) ||
      (!dayHasWorkItemForProject && !projectStart && projectEndsAfterOrOnDay)
    ) {
      const workItem = createWorkItem(project, null, {
        start: projectStart,
        end: projectEnd,
      })
      schedule[i].workItems.push(workItem)
    }
  }
}

function addProjectDateWorkItemsToSchedule(
  project: Project,
  projectDate: ProjectDate,
  schedule: TSchedule,
) {
  const scheduleStartDate = DateTime.fromISO(schedule[0].date).startOf('day')
  const scheduleEndDate = DateTime.fromISO(
    schedule[schedule.length - 1].date,
  ).endOf('day')

  const projectDateStart = projectDate.start_date
    ? DateTime.fromISO(projectDate.start_date)
    : undefined
  const projectDateEnd = projectDate.end_date
    ? DateTime.fromISO(projectDate.end_date)
    : undefined

  if (projectDate.rrule) {
    const projectDateRecurrences = getRecurringWorkItems(
      project,
      projectDate,
      scheduleStartDate,
      scheduleEndDate,
    )
    for (const projectDateRecurrence of projectDateRecurrences) {
      addToSchedule(projectDateRecurrence, schedule)
    }
  }

  const workItem = createWorkItem(project, projectDate, {
    start: projectDateStart,
    end: projectDateEnd,
  })
  addToSchedule(workItem, schedule)
}

function getRecurringWorkItems(
  project: Project,
  projectDate: ProjectDate | null,
  scheduleStart: DateTime,
  scheduleEnd: DateTime,
) {
  const recurringEntity = projectDate ?? project
  const workItems = []
  const start = recurringEntity.start_date
    ? DateTime.fromISO(recurringEntity.start_date)
    : null
  const end = recurringEntity.end_date
    ? DateTime.fromISO(recurringEntity.end_date)
    : null

  const projectRecurrences: { start: DateTime; end: DateTime }[] =
    recurringEntity.rrule
      ? OccurrenceGenerator.getOccurrences(
          scheduleStart,
          scheduleEnd,
          recurringEntity.rrule,
          start ?? undefined,
          end ?? undefined,
        )
      : []

  const projectRecurrencesByDay = groupBy(projectRecurrences, (rec) =>
    rec.start.startOf('day').toISO(),
  )

  for (const [, recurrences] of Object.entries(projectRecurrencesByDay)) {
    for (const recurrence of recurrences) {
      const recurrenceIsAfterProjectEndDate = end && end <= recurrence.start
      if (!recurrenceIsAfterProjectEndDate) {
        continue
      }
      const workItem = createWorkItem(project, projectDate, recurrence)
      workItems.push(workItem)
    }
  }
  return workItems
}

function addToSchedule(workItem: TWorkItem, schedule: TSchedule) {
  if (!workItem.start && !workItem.end) return
  const from = workItem.start ?? DateTime.fromISO(schedule[0].date)
  let to = DateTime.fromISO(schedule[0].date)
  if (workItem.start && workItem.end) {
    if (workItem.start.toMillis() === workItem.end.toMillis()) {
      // sometimes start and end are the same time, the interval.fromDateTimes will not work here so we add one minute
      // to make sure at least one day gets created.
      to = workItem.start.plus({ minutes: 1 })
    } else {
      to = workItem.end
    }
  }

  const days = Interval.fromDateTimes(from, to)
    .splitBy({ day: 1 })
    .map((d) => d.start.startOf('day'))

  const scheduleDays = schedule.filter(
    (scheduleDay) =>
      scheduleDay.date ===
      days.find((d) => d.toISO() === scheduleDay.date)?.toISO(),
  )

  for (const scheduleDay of scheduleDays) {
    if (workItem.type === WorkItemType.PROJECT) {
      const dayHasWorkItemForProject = scheduleDay.workItems.find(
        (item) => item.project_id === workItem.project_id,
      )
      if (dayHasWorkItemForProject) {
        continue
      }
    }
    const workItemAlreadyExists = scheduleDay.workItems.find(
      (item) =>
        item.start?.toMillis() === workItem.start?.toMillis() &&
        item.end?.toMillis() === workItem.end?.toMillis() &&
        item.type === workItem.type &&
        item.project_id === workItem.project_id &&
        item.project_date_id === workItem.project_date_id,
    )
    if (!workItemAlreadyExists) {
      scheduleDay.workItems.push(workItem)
    }
  }
}

function createWorkItem(
  project: Project,
  projectDate?: ProjectDate | null,
  times?: { start?: DateTime; end?: DateTime } | null,
): TWorkItem {
  return {
    start: times?.start ?? null,
    end: times?.end ?? null,
    title: project.name,
    subtitle: projectDate?.name || null,
    customer: project.customer || null,
    number: projectDate?.number || project.number || null,
    location: projectDate?.location || project.destination_city,
    project_id: project.id,
    project_date_id: projectDate?.id || null,
    type: projectDate ? WorkItemType.DATE : WorkItemType.PROJECT,
    completed_date:
      project.completed_date ?? projectDate?.completed_date ?? null,
  }
}
